import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  Typography,
  styled,
  TextField,
  Grid,
} from '@mui/material';

export const SectionTitle = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '14px',
}));

export const ActionBar = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  paddingTop: '16px',
  borderTop: '1px solid #EBEBEB',
  marginTop: '8px',
}));

export const ApproveRejectBar = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '24px',
}));

export const EditButton = styled(Button)(({ theme }) => ({
  width: '200px',
  display: 'flex',
  alignContent: 'center',
  gap: '8px',
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: '12px',
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  textTransform: 'lowercase',
  '&:disabled': {
    color: theme.palette.secondary.main,
  },
  span: { marginTop: '2px' },
}));

export const SaveButton = styled(Button)(() => ({
  borderRadius: '12px',
  width: '200px',
}));

export const RejectButton = styled(Button)(() => ({
  width: '200px',
  backgroundColor: '#FAFCFF',
  fontWeight: 600,
  fontSize: 15,
  lineHeight: '20px',
  border: '2px solid #FF334B',
  color: '#FF334B',
  display: 'flex',
  gap: 8,
  '&:hover': {
    backgroundColor: '#FF334B',
  },
  '&:active': {
    backgroundColor: '#FF334B',
  },
  '&:disabled': {
    color: '#FF334B',
  },
}));

export const ApproveButton = styled(Button)(({ theme }) => ({
  width: '200px',
  backgroundColor: theme.palette.primary.dark,
  color: '#FFF',
  textTransform: 'lowercase',
  '&:hover': { filter: 'brightness(85%)' },
  '&:disabled': { opacity: '70%' },
}));

export const RegisterBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px',
  border: `1px dashed #ADADAD`,
  borderRadius: '12px',
  marginTop: '12px',
}));

export const DocumentBox = styled(Box)(() => ({
  padding: '20px',
  border: `1px dashed #ADADAD`,
  borderRadius: '12px',
  marginTop: '12px',
}));

export const InputTitle = styled(Typography)(() => ({
  paddingBottom: '4px',
  fontSize: '14px',
  textTransform: 'capitalize',
}));

export const Content = styled(Typography)(() => ({
  fontWeight: '500',
  textTransform: 'capitalize',
}));

export const CompanionBox = styled(Box)(() => ({
  border: '1px solid #D7D8DB',
  borderRadius: '8px',
  padding: '16px',
}));

export const FileList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: 0,
  listStyleType: 'none',
  margin: 0,
  '& span': { color: theme.palette.secondary.main },
}));

export const SubmitFileBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '24px',
  margin: '24px 0',
  width: '100%',
}));

export const CancelFileButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  fontWeight: '700',
  color: theme.palette.secondary.main,
  border: `2px solid ${theme.palette.secondary.main}`,
  borderRadius: '30px',
  width: '150px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));

export const SubmitFileButton = styled(Button)(({ theme }) => ({
  width: '150px',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    filter: 'brightness(85%)',
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));

export const TextTypography = styled(Typography)(() => ({
  textAlign: 'center',
  fontSize: '18px',
  color: '#595959',
}));

export const RejectTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    '&::-webkit-scrollbar': {
      width: 10,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: 10,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  },
}));

export const FileContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  borderBottom: '1px solid #ccc',
  padding: '8px 0 8px 4px',
  '&:last-child': { borderBottom: 'none' },
}));

export const AttachmentsBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const DialogTitle = styled(Typography)(({ theme }) => ({
  font: '700 21px Montserrat',
  textTransform: 'capitalize',
  color: '#595959',
  marginBottom: 24,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '& span': { color: theme.palette.secondary.main },
}));

export const FileNameTypography = styled(Typography)(({ theme }) => ({
  minWidth: '300px',
  display: 'flex',
  gap: '6px',
  alignContent: 'center',
  color: '#595959',
  font: '400 16px Montserrat',
  '& span': {
    color: theme.palette.secondary.main,
    fontSize: 20,
  },
}));

export const StyledIcon = styled(ButtonBase)(({ theme }) => ({
  span: {
    color: theme.palette.secondary.main,
    fontSize: 20,
    padding: '4px',
    borderRadius: '50%',
  },
}));

export const InputBox = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  marginTop: '24px',
}));

const statusColors: Record<string, string> = {
  approved: '#2e9133',
  rejected: '#992626',
};

export const StatusTag = styled(Typography)<{ status: string }>(
  ({ status }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    border: `2px solid ${statusColors[status] ?? '#595959'}`,
    borderRadius: '10px',
    padding: '4px 16px',
    color: statusColors[status] ?? '#595959',
    fontWeight: '600',
    textTransform: 'capitalize',
  })
);

export const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    backgroundColor: '#FAFCFF',
  },
}));

export const DialogContainer = styled(Box)(() => ({
  minWidth: '600px',
  minHeight: '200px',
  padding: '40px 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
}));

export const ButtonBox = styled(Box)(() => ({
  margin: '20px 0',
  display: 'flex',
  gap: '40px',
  justifyContent: 'center',
}));

export const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  width: '100%',
  border: `1px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  fontWeight: '600',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const OutlinedButton = styled(Button)(({ theme }) => ({
  maxWidth: '150px',
  border: `1.5px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  textTransform: 'lowercase',
}));

export const FilledButton = styled(Button)(({ theme }) => ({
  maxWidth: '150px',
  backgroundColor: theme.palette.secondary.main,
  textTransform: 'lowercase',
  '&:hover': {
    filter: 'brightness(85%)',
  },
}));

type AttachFileButtonProps = {
  component: string;
};

export const AttachFileButon = styled(Button)<AttachFileButtonProps>(
  ({ component, theme }) => ({
    component,
    width: '170px',
    margin: '10px',
    color: '#FFFFFF',
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      filter: 'brightness(85%)',
    },
    [theme.breakpoints.up('xl')]: {
      width: '220px',
    },
    '&:disabled': {
      filter: 'opacity(50%)',
    },
  })
);
